/**
 * @file
 *
 * Wrapper around fetch(), and OAuth access token handling operations.
 *
 * To use import getAuthClient, and initialize a client:
 * const auth = getAuthClient(optionalConfig);
 */
import {IPrice} from "../commerce/Cart";
import axios from "axios";
import useLocalStorageState from 'use-local-storage-state'
import Config from "../../utils/Config";

const refreshPromises = [];

export interface IRequestError {
  statusCode: number;
  url: string;
  responseData: any;
}
export interface IUserStatus {
  mail?: string;
  name?: string;
  id?: string;
}

export interface IAuthConfig {
  base: string;
  token_name: string;
  client_id: string;
  client_secret: string;
  scope: string;
  expire_margin: number;
}

export interface IRequestUserLogin {
  current_user: IUserStatus;
  csrf_token: string;
  logout_token: string;
  redirect_url?: string;
}

export interface IRequestUserStatus {
  current_user: IUserStatus;
  is_authenticated: boolean;
  csrf_token?: string;
  logout_token?: string;
}

const defaultAuthConfig = {
  // Base URL of your Drupal site.
  base: Config.getDrupalVar('path.baseRoot'),

} as IAuthConfig;

/**
 * Rest Auth client factory.
 *
 *
 * @returns {object}
 *   Returns an object of functions with $config injected.
 * @param config
 */
export function getAuthClient(config = defaultAuthConfig) {
  config =  {...defaultAuthConfig, ...config} as IAuthConfig

  /**
   * Login with username & password
   *
   * @param {string} username
   * @param {string} password
   */
  async function login(username, password) {
    const loginUrl = config.base + '/user/login';
    const authData= {
      name: username,
      pass: password
    }
    return axios.post<IRequestUserLogin>(loginUrl + '?_format=json', authData)
      .then(response => {
        window.localStorage.setItem('csrfToken', JSON.stringify(response.data.csrf_token));
        window.localStorage.setItem('logoutToken', JSON.stringify(response.data.logout_token));
        axios.defaults.withCredentials = true;
        axios.defaults.headers.common['X-CSRF-TOKEN'] = response.data.csrf_token;
        return response;
    }).then(response => {
        return retrieveUserStatus();
    }).catch(error => {
      console.log('Error on login', error);
      return Promise.reject(new Error(`Error on login: ${error.response.data.message}`));
    });
  }

  /**
   * Register with username & password
   *
   * @param {string} username
   * @param {string} password
   */
  async function register(username, password) {
    const loginUrl = config.base + '/user/register';
    const authData= {
      name: username,
      pass: password,
      mail: username,
      field_agb: true,
    }
    return axios.post(loginUrl + '?_format=json', authData)
      .then(response => {
        return login(username, password);
      }).catch(error => {
        console.log('Error on register', error);
        return Promise.reject(new Error(`Error on register: ${error.response.data.message}`));
      });
  }

  /**
   * Delete the stored OAuth token, effectively ending the user's session.
   */
  function logout() {
    const token = JSON.parse(window.localStorage.getItem('logoutToken'));
    return axios.get(config.base + '/user/logout?_format=json&token=' + token)
      .then((res) => {
        console.log('user logout successful');
        this.setCsrfToken('');
      })
      .catch((e) => {
        // this.userLoggedIn = true;
        console.error('user logout failed', e);
      });
  }

  function retrieveUserStatus(): Promise<IRequestUserStatus> {
    return new Promise<IRequestUserStatus>((resolve: (resp: IRequestUserStatus) => void, reject: (resp: IRequestError) => void) => {
      // if (this.userStatus === null || this.userLoggedIn !== true) {

      return axios.get(config.base + '/myapi/user/user-status')
        .then((res) => {
          const data: IRequestUserStatus = res.data;

          return resolve(data);

        })
        .catch((e) => {
          console.error(e);
          reject(e);
        });
    });
  }

  return {login, logout, register, retrieveUserStatus};
}
